import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO — declarative <head> manager for Silk & Sentiment.
 *
 * Usage:
 *   <SEO
 *     title="About Us"
 *     description="..."
 *     path="/about"
 *     image="https://..." // optional override
 *     type="article"     // optional, defaults to 'website'
 *     schema={{...}}     // optional JSON-LD
 *     noIndex            // set to hide from search engines (admin/track)
 *   />
 */

const SITE_NAME = 'Silk & Sentiment';
const SITE_TAGLINE = 'Elegance in every thread';
const DEFAULT_DESCRIPTION =
  'Silk & Sentiment is a luxury Indian couture house, hand-crafting bespoke silk garments in Lucknow. Heritage zardozi, chikankari and aari embroidery, curated for the world.';
// Hero brand image used as default OG card
const DEFAULT_OG_IMAGE =
  'https://customer-assets.emergentagent.com/job_568566e0-ffb3-4a84-a1fa-2157a3f7e73e/artifacts/o01249hr_765D7045-41FA-4965-8076-193AD168A5CB.png';

const getOrigin = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return 'https://silkandsentiment.com';
};

const buildTitle = (title) => {
  if (!title) return `${SITE_NAME} \u2014 ${SITE_TAGLINE}`;
  if (title === SITE_NAME) return `${SITE_NAME} \u2014 ${SITE_TAGLINE}`;
  return `${title} \u2014 ${SITE_NAME}`;
};

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  schema,
  noIndex = false,
}) => {
  const fullTitle = buildTitle(title);
  const url = `${getOrigin()}${path || ''}`;
  const desc = (description || DEFAULT_DESCRIPTION).replace(/\s+/g, ' ').trim().slice(0, 300);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noIndex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
};

export const ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: 'https://silkandsentiment.com',
  logo: 'https://silkandsentiment.com/logo.png',
  email: 'info@silkandsentiment.com',
  telephone: '+91-9151-060-995',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Lucknow',
    addressRegion: 'Uttar Pradesh',
    addressCountry: 'IN',
  },
  sameAs: [
    'https://wa.me/919151060995',
  ],
  description: DEFAULT_DESCRIPTION,
};

export const websiteSchema = (origin) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: origin || 'https://silkandsentiment.com',
});

export const productSchema = (product, origin) => {
  if (!product) return null;
  const base = origin || getOrigin();
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: (product.images && product.images.length ? product.images : [product.image]).filter(Boolean),
    brand: { '@type': 'Brand', name: SITE_NAME },
    category: product.type === 'archive' ? 'Heritage Couture' : 'Ready to Wear',
    material: product.fabric || undefined,
    url: `${base}/${product.type === 'archive' ? 'archive' : 'shop'}/${product.id}`,
    offers: product.price
      ? {
          '@type': 'Offer',
          priceCurrency: 'INR',
          price: product.price,
          availability:
            product.status === 'Available'
              ? 'https://schema.org/InStock'
              : product.status === 'Limited'
                ? 'https://schema.org/LimitedAvailability'
                : 'https://schema.org/PreOrder',
          seller: { '@type': 'Organization', name: SITE_NAME },
        }
      : undefined,
  };
};

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

export default SEO;
